/**
 * The Eingang's files, from the real routes (P1.1).
 *
 * Teil 1 reads the Eingang's stored files (GET /posteingang/:commId/attachments), which
 * works for a task-free Eingang too - the split view does not. Teil 4 reads the per-task
 * Versandanhaenge (GET /tasks/:id/attachments), which carry the channel and the on/off
 * state the mask used to hold in session state.
 *
 * Both are server-gated: below tier full the list is empty (D3), so the mask renders what
 * it is given and never decides for itself whether a file may be shown.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AttachmentView, TaskAttachmentPatch, TaskAttachmentView } from '@brokly/shared';
import { apiClient } from '../../../lib/api-client';
import { isLiveData } from '../../../lib/data-mode';
import { qk } from '../../../lib/query-keys';
import { eingangAttachmentsMock } from '../mock';

/** Teil 1: the files of one Eingang. */
export function useEingangAttachments(commId: string | null) {
  const live = isLiveData();
  return useQuery<AttachmentView[]>({
    queryKey: qk.eingangAttachments(commId ?? 'none', live ? 'live' : 'mock'),
    queryFn: async () => {
      if (commId === null) return [];
      if (!live) return eingangAttachmentsMock(commId);
      return (await apiClient.posteingang.attachments(commId)).data;
    },
    enabled: commId !== null,
    ...(live ? {} : { staleTime: Infinity }),
  });
}

/** The mock leg: the Eingang's own files, every one active on E-Mail. */
function mockTaskAttachments(commId: string | null, taskId: string): TaskAttachmentView[] {
  if (commId === null) return [];
  return eingangAttachmentsMock(commId).map((a) => ({
    task_id: taskId,
    document_id: a.id,
    filename: a.filename,
    mime_type: a.mime_type,
    size_bytes: a.size_bytes,
    delivery_channel: 'email' as const,
    channel_overridden: false,
    is_active: true,
    deactivated_at: null,
    deactivated_by_user: false,
  }));
}

/** Teil 4: the Versandanhaenge of one task. Seeded server-side on first read. */
export function useTaskAttachments(taskId: string | null, commId: string | null) {
  const live = isLiveData();
  return useQuery<TaskAttachmentView[]>({
    queryKey: qk.taskAttachments(taskId ?? 'none', live ? 'live' : 'mock'),
    queryFn: async () => {
      if (taskId === null) return [];
      if (!live) return mockTaskAttachments(commId, taskId);
      return (await apiClient.tasks.attachments(taskId)).data;
    },
    enabled: taskId !== null,
    retry: false,
    ...(live ? {} : { staleTime: Infinity }),
  });
}

/** PATCH one Versandanhang. Mock mode writes to the query cache, so the UI still responds. */
export function usePatchTaskAttachment(taskId: string | null) {
  const live = isLiveData();
  const qc = useQueryClient();
  const key = qk.taskAttachments(taskId ?? 'none', live ? 'live' : 'mock');
  return useMutation({
    mutationFn: async ({
      documentId,
      patch,
    }: {
      documentId: string;
      patch: TaskAttachmentPatch;
    }): Promise<TaskAttachmentView | null> => {
      if (taskId === null) return null;
      if (!live) return null;
      return apiClient.tasks.patchAttachment(taskId, documentId, patch);
    },
    onSuccess: (updated, { documentId, patch }) => {
      // Key off the task the SERVER echoes, not the hook's possibly-stale `taskId`: a PATCH
      // that resolves after the broker switched Aufgabe must land in ITS cache, not the new
      // one (sibling tasks of one Eingang share document_ids, so the wrong write would "take").
      // `updated` is null in mock (no async race there) → fall back to the render-time key.
      const targetKey = updated
        ? qk.taskAttachments(updated.task_id, live ? 'live' : 'mock')
        : key;
      qc.setQueryData<TaskAttachmentView[]>(targetKey, (rows) =>
        (rows ?? []).map((row) =>
          row.document_id !== documentId
            ? row
            : (updated ?? {
                ...row,
                ...patch,
                // A channel-only patch carries no is_active, and must not go on to claim
                // that nobody switched the file off.
                deactivated_by_user:
                  patch.is_active === undefined ? row.deactivated_by_user : !patch.is_active,
              }),
        ),
      );
    },
  });
}
